import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Pagination } from "@/components/common/Pagination";
import { AgencyBlogModal } from "@/components/admin/AgencyBlogModal";
import { ConfirmationModal } from "@/components/ui/confirmation-modal";
import { RichTextPreview } from "@/components/Editor/RichTextPreview";
import { Plus, Edit2, Trash2, Calendar } from "lucide-react";
import { formatDate } from "@/utils/formatters";
import {
  adminAgencyBlogService,
  type AgencyBlogResponseDto,
  type PaginatedAgencyBlogResponse,
} from "@/services/admin/agencyBlog.admin.service";

export default function AgencyBlog() {
  const [blogs, setBlogs] = useState<AgencyBlogResponseDto[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBlog, setEditingBlog] = useState<AgencyBlogResponseDto | null>(
    null
  );
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadBlogs = async (page: number = 1) => {
    setLoading(true);
    try {
      const response: PaginatedAgencyBlogResponse =
        await adminAgencyBlogService.list(page, pagination.limit);
      setBlogs(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error("Error loading blogs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBlogs(pagination.page);
  }, []);

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    loadBlogs(newPage);
  };

  const handleCreate = () => {
    setEditingBlog(null);
    setModalMode("create");
    setIsModalOpen(true);
  };

  const handleEdit = (blog: AgencyBlogResponseDto) => {
    setEditingBlog(blog);
    setModalMode("edit");
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    setLoading(true);
    setDeletingId(id);
    try {
      await adminAgencyBlogService.remove(id);
      setBlogs((prev) => prev.filter((blog) => blog.id !== id));
      setPagination((prev) => ({ ...prev, total: prev.total - 1 }));
    } catch (error) {
      console.error("Error deleting blog:", error);
    } finally {
      setLoading(false);
      setDeletingId(null);
    }
  };

  const handleModalSuccess = () => {
    loadBlogs(pagination.page);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingBlog(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Agency Blog</h1>
          <p className="text-muted-foreground mt-1">
            Manage agency blog posts and content
          </p>
        </div>
        <Button
          onClick={handleCreate}
          className="bg-[#d7785b] hover:bg-[#d7785b]/80 flex items-center gap-2"
        >
          <Plus size={20} />
          Create Blog Post
        </Button>
      </div>


      {/* Blog List */}
      <div className="space-y-4">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, index) => (
              <Card key={index} className="animate-pulse">
                <CardHeader>
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                  <div className="h-3 bg-muted rounded w-1/2"></div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="h-3 bg-muted rounded"></div>
                    <div className="h-3 bg-muted rounded w-5/6"></div>
                    <div className="h-3 bg-muted rounded w-4/6"></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : blogs.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="text-muted-foreground">
                <Calendar size={48} className="mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No blog posts yet</h3>
                <p className="text-sm mb-4">
                  Get started by creating your first blog post.
                </p>
                <Button
                  onClick={handleCreate}
                  className="bg-[#d7785b] hover:bg-[#c86c51]"
                >
                  <Plus size={20} className="mr-2" />
                  Create Blog Post
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {blogs.map((blog) => (
              <Card key={blog.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg line-clamp-2 mb-2">
                        {blog.title}
                      </CardTitle>
                      <div className="mt-4 flex items-center justify-between">
                    <Badge variant="secondary" className="text-xs">
                      Last Update: {formatDate(blog.updatedAt)}
                    </Badge>
                  </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(blog)}
                        className="h-8 w-8 p-0"
                      >
                        <Edit2 size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeletingId(blog.id)}
                        className="h-8 w-8 p-0 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                <div className="prose !max-h-[2x00px] overflow-hidden prose-sm max-w-none">
                    <RichTextPreview
                      content={blog.description}
                      className="line-clamp-3"
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
        <div className="flex justify-center">
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            onPageChange={handlePageChange}
          />
        </div>

      {/* Modals */}
      <AgencyBlogModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleModalSuccess}
        blog={editingBlog}
        mode={modalMode}
      />

      <ConfirmationModal
        isOpen={!!deletingId}
        onClose={() => setDeletingId(null)}
        onConfirm={() => deletingId && handleDelete(deletingId)}
        title="Delete Blog Post"
        message="Are you sure you want to delete this blog post? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        isLoading = {loading}
      />
    </div>
  );
}
